<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  modelValue: { type: String, default: 'recibo' },
  file: { type: Object, default: null },
  currentDocument: { type: Object, default: null },
  saving: { type: Boolean, default: false },
  progress: { type: Number, default: 0 },
  canSubmit: { type: Boolean, default: false },
  canDownload: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:modelValue',
  'update:file',
  'submit',
  'download',
]);
const { t } = useI18n();
const uploadTitle = computed(() =>
  props.modelValue === 'nfe'
    ? t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.UPLOAD_TITLE_NFE')
    : t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.UPLOAD_TITLE_RECEIPT')
);
const handleFile = event =>
  emit('update:file', event.target.files?.[0] || null);
</script>

<template>
  <div data-testid="broker-tax-document-form" class="space-y-5">
    <LaisCard :title="t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.TYPE_TITLE')">
      <div class="space-y-3">
        <label
          v-for="option in ['recibo', 'nfe']"
          :key="option"
          class="flex cursor-pointer items-center gap-3 rounded-lais border p-4"
          :class="
            modelValue === option
              ? 'border-lais-primary bg-lais-primary-50/40'
              : 'border-lais-border'
          "
        >
          <input
            :checked="modelValue === option"
            class="size-4 accent-lais-primary"
            name="broker-tax-document-kind"
            type="radio"
            :value="option"
            @change="emit('update:modelValue', option)"
          />
          <span class="font-semibold text-lais-text">
            {{
              option === 'nfe'
                ? t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.NFE')
                : t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.RECEIPT')
            }}
          </span>
        </label>
      </div>
    </LaisCard>
    <LaisCard :title="uploadTitle">
      <div class="space-y-4">
        <p class="text-sm text-lais-text-body">
          {{ t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.UPLOAD_HELPER') }}
        </p>
        <input
          accept="application/pdf,image/jpeg,image/png,.doc,.docx"
          class="block w-full text-sm text-lais-text"
          type="file"
          @change="handleFile"
        />
        <p
          v-if="file"
          class="rounded-lais bg-lais-bg-soft p-3 text-sm text-lais-text"
        >
          {{ file.name }}
        </p>
        <p v-if="currentDocument" class="text-sm text-lais-text-muted">
          {{
            t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.CURRENT', {
              name: currentDocument.nome,
            })
          }}
        </p>
        <progress
          v-if="saving"
          class="h-2 w-full overflow-hidden rounded-full accent-lais-primary"
          :value="progress"
          max="100"
        />
        <div class="flex flex-wrap gap-3">
          <LaisPrimaryButton
            v-if="file"
            icon="i-lucide-upload"
            :disabled="!canSubmit"
            :is-loading="saving"
            data-testid="broker-tax-document-submit"
            @click="emit('submit')"
          >
            {{ t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.SEND') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            v-if="currentDocument && canDownload"
            icon="i-lucide-download"
            variant="ghost"
            @click="emit('download')"
          >
            {{ t('REAL_ESTATE_BROKER_COMMISSION.DOCUMENT.DOWNLOAD') }}
          </LaisPrimaryButton>
        </div>
      </div>
    </LaisCard>
  </div>
</template>
